import { Button, useStyles, useTheme } from '@archipro-design/aria';
import { Tag } from '@archipro-design/icons';
import React, { useEffect } from 'react';
import { useProductTagging } from '../../context';
import useModalPosition from '../../hooks/use-modal-position';
import type { TagDataUseToCreateTag, TagPosition } from '../../type';
import { convertTagPosition } from '../../util/convert-tag-position';
import SupplierDetailsForm from '../supplier-details-form/SupplierDetailsForm';
import ProductTaggingSearchModal from '../product-tagging-search/ProductTaggingSearchModal';
import * as S from './ProductTaggingNewTag.style';

interface ProductTaggingNewTagProps {
    tagPosition: TagPosition;
    show: boolean;
    boxRef: Element;
    onCreateMarker: (data: TagDataUseToCreateTag) => void;
}

const ProductTaggingNewTag = ({
    tagPosition,
    boxRef,
    onCreateMarker,
}: ProductTaggingNewTagProps) => {
    const { css } = useStyles();
    const theme = useTheme();
    const {
        state: { isAddingSupplier },
    } = useProductTagging();

    const {
        setReferenceElement,
        setPopperElement,
        floatingStyles,
        update,
        isPositioned,
    } = useModalPosition(boxRef);

    // Each time the tagPosition or isAddingSupplier changes, update the position of the popper
    useEffect(() => {
        if (update) update();
    }, [update, tagPosition, isAddingSupplier]);

    const { pctXAfterOffset, pctYAfterOffset } = convertTagPosition(
        tagPosition.pctX,
        tagPosition.pctY,
        tagPosition.rectWidth,
        tagPosition.rectHeight,
    );

    return (
        <>
            <div
                ref={setPopperElement}
                style={floatingStyles}
                className={`${css(S.NewTagModal)} z-popover`}
            >
                {isAddingSupplier ? (
                    <SupplierDetailsForm
                        onSubmit={(data) => onCreateMarker(data)}
                    />
                ) : (
                    <ProductTaggingSearchModal
                        onCreateMarker={onCreateMarker}
                        isPositioned={isPositioned}
                    />
                )}
            </div>
            <Button
                size={20}
                variables={{
                    iconPadding: 0,
                    buttonPadding: 12,
                    buttonRadius: '50%',
                }}
                transparent={true}
                icon={
                    <span className={css(S.TagIconOnPhoto)}>
                        <Tag
                            color={
                                theme.siteVariables.naturalColors.white['000']
                            }
                        />
                    </span>
                }
                className={css(S.CurrentTag)}
                ref={setReferenceElement}
                style={{
                    top: `${pctYAfterOffset}%`,
                    left: `${pctXAfterOffset}%`,
                    transform: 'translate(-50%, -50%)',
                }}
            />
        </>
    );
};

export default ProductTaggingNewTag;
